import { useEffect, useState } from 'react';
import { Alert, Box, Button, IconButton, Stack, TextField, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import * as yup from 'yup';
import { Header } from '../../../../Shared/Header/Header';
import { AvlModal } from '../../../../Shared/AvlModal/AvlModal';
import ConfirmationModal from '../../../../Shared/ConfirmationModal';
import SkeletonComponent from '../../../../SkeletonComponent';
import { datetimeFormat } from '../../../../../Constants/Date.constant';
import { addressLinePattern, postcodePattern } from '../../../../../Constants/Validation.constant';
import { AvlTable } from '../../../../Shared/Table/Table';
import format from 'date-fns/format';
import {
    getOrgAddresses,
    createOrgAddress,
    updateOrgAddress,
    deleteOrgAddress,
} from '../../../../../Services/Search.services';

const addressSchema = yup.object({
    name: yup.string().trim().required('Name is required.'),
    addressLines: yup
        .string()
        .trim()
        .required('At least one address line is required.')
        .matches(addressLinePattern, 'Each address line must include standard characters only.')
        .test('line-max-length', 'Each address line must be 35 characters or fewer.', (value) =>
            (value ?? '').split('\n').every((line) => line.trim().length <= 35)
        )
        .test('max-lines', 'Maximum 6 address lines allowed (5 address lines + city).', (value) =>
            (value ?? '').split('\n').filter((line) => line.trim().length > 0).length <= 6
        ),
    postCode: yup
        .string()
        .trim()
        .required('Post code is required.')
        .matches(postcodePattern, 'Enter a valid post code (e.g. SW1A 1AA).'),
});

const tableHeaders = [
    { label: 'Name' },
    { label: 'Address Lines' },
    { label: 'Post Code' },
    { label: 'Added At' },
    { label: 'Actions', align: 'center' },
];

const emptyForm = { name: '', addressLines: '', postCode: '' };
const createTouched = (value) => Object.fromEntries(Object.keys(emptyForm).map((key) => [key, value]));
const emptyTouched = createTouched(false);

export const OrgAddressesTab = ({ orgName }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [addresses, setAddresses] = useState([]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editTargetAddress, setEditTargetAddress] = useState(null);
    const [formValues, setFormValues] = useState(emptyForm);
    const [initialFormValues, setInitialFormValues] = useState(emptyForm);
    const [formErrors, setFormErrors] = useState({});
    const [touched, setTouched] = useState(emptyTouched);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const fetchAddresses = async () => {
        setIsLoading(true);

        try {
            const res = await getOrgAddresses(orgName);
            const data = res?.data ?? [];

            setAddresses([...data].sort((a, b) => new Date(b.added_at) - new Date(a.added_at)));
        } catch {
            setAddresses([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAddresses();
    }, [orgName]);

    const handleOpenAddModal = () => {
        setEditTargetAddress(null);
        setFormValues(emptyForm);
        setInitialFormValues(emptyForm);
        setFormErrors({});
        setTouched(emptyTouched);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (address) => {
        const values = {
            name: address.name || '',
            addressLines: (address.address_lines || []).join('\n'),
            postCode: address.post_code || '',
        };

        setEditTargetAddress(address);
        setFormValues(values);
        setInitialFormValues(values);
        setFormErrors({});
        setTouched(emptyTouched);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        if (isSubmitting) return;
        
        setIsModalOpen(false);
    };

    const handleFormChange = (field) => async (e) => {
        const value = e.target.value;
        setFormValues((prev) => ({ ...prev, [field]: value }));

        if (touched[field]) {
            try {
                await addressSchema.validateAt(field, { ...formValues, [field]: value });
                setFormErrors((prev) => ({ ...prev, [field]: undefined }));
            } catch (err) {
                setFormErrors((prev) => ({ ...prev, [field]: err.message }));
            }
        }
    };

    const handleBlur = (field) => async () => {
        setTouched((prev) => ({ ...prev, [field]: true }));

        try {
            await addressSchema.validateAt(field, formValues);
            setFormErrors((prev) => ({ ...prev, [field]: undefined }));
        } catch (err) {
            setFormErrors((prev) => ({ ...prev, [field]: err.message }));
        }
    };

    const buildPayload = (values) => ({
        name: values.name.trim(),
        addressLines: values.addressLines
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean),
        postCode: values.postCode.trim(),
    });

    const handleSubmit = async () => {
        try {
            await addressSchema.validate(formValues, { abortEarly: false });
        } catch (validationError) {
            const errors = {};
            validationError.inner.forEach((err) => {
                errors[err.path] = err.message;
            });

            setFormErrors(errors);
            setTouched(createTouched(true));

            return;
        }

        setIsSubmitting(true);

        try {
            const payload = buildPayload(formValues);
            const res = await (editTargetAddress
                ? updateOrgAddress(orgName, editTargetAddress.id, payload)
                : createOrgAddress(orgName, payload));
            if (res && !res.stack) {
                setIsModalOpen(false);
                fetchAddresses();
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOpenDeleteModal = (address) => {
        setDeleteTarget(address);
        setIsDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setDeleteTarget(null);
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;

        try {
            const res = await deleteOrgAddress(orgName, deleteTarget.id);
            if (res && !res.stack) fetchAddresses();
        } finally {
            handleCloseDeleteModal();
        }
    };

    const isFormUnchanged =
        !!editTargetAddress
        && formValues.name === initialFormValues.name
        && formValues.addressLines === initialFormValues.addressLines
        && formValues.postCode === initialFormValues.postCode;

    const isSaveDisabled =
        isSubmitting
        || isFormUnchanged
        || !formValues.name.trim()
        || !formValues.addressLines.trim()
        || !formValues.postCode.trim()
        || Object.values(formErrors).some(Boolean);

    const getFieldError = (field) => (touched[field] ? formErrors[field] : undefined);

    const tableRows = addresses.map((address) => ({
        cells: [
            { data: address.name || '-' },
            {
                data: (address.address_lines || []).map((line, i) => (
                    <div key={i}>{line}</div>
                )),
            },
            { data: address.post_code || '-' },
            {
                data: address.added_at
                    ? format(new Date(address.added_at), datetimeFormat)
                    : '-',
            },
            {
                align: 'center',
                data: (
                    <>
                        <Tooltip title='Edit'>
                            <IconButton 
                                size='small' 
                                onClick={() => handleOpenEditModal(address)}
                            >
                                <EditIcon fontSize='small' />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title='Delete'>
                            <IconButton
                                size='small'
                                color='error'
                                onClick={() => handleOpenDeleteModal(address)}
                            >
                                <DeleteIcon fontSize='small' />
                            </IconButton>
                        </Tooltip>
                    </>
                ),
            },
        ],
    }));

    return (
        <>
            <div className='header'>
                <Header
                    title='Addresses'
                    actionButtons={[
                        <Button
                            key='add-address-button'
                            variant='contained'
                            size='small'
                            color='primary'
                            startIcon={<AddIcon />}
                            onClick={handleOpenAddModal}
                        >
                            Add address
                        </Button>,
                    ]}
                />
            </div>

            {isLoading ? (
                <SkeletonComponent />
            ) : addresses.length ? (
                <AvlTable
                    headers={tableHeaders}
                    rows={tableRows}
                    isPaginationDisabled
                    pageCount={1}
                    page={1}
                    onPageChange={() => {}}
                />
            ) : (
                <Stack 
                    sx={{ width: '100%' }} 
                    spacing={2}
                >
                    <Alert severity='info'>No addresses found. Use &quot;Add Address&quot; to create one.</Alert>
                </Stack>
            )}

            <AvlModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={editTargetAddress ? 'Edit Address' : 'Add Address'}
                maxWidth='500px'
                actionButtons={[
                    <Button 
                        key='cancel' 
                        onClick={handleCloseModal} 
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>,
                    <Button
                        key='save'
                        variant='contained'
                        onClick={handleSubmit}
                        disabled={isSaveDisabled}
                    >
                        {isSubmitting ? 'Saving...' : 'Save'}
                    </Button>,
                ]}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField
                        label='Name'
                        value={formValues.name}
                        onChange={handleFormChange('name')}
                        onBlur={handleBlur('name')}
                        error={!!getFieldError('name')}
                        helperText={getFieldError('name')}
                        size='small'
                        fullWidth
                        required
                    />
                    <TextField
                        label='Address Lines'
                        value={formValues.addressLines}
                        onChange={handleFormChange('addressLines')}
                        onBlur={handleBlur('addressLines')}
                        error={!!getFieldError('addressLines')}
                        helperText={getFieldError('addressLines') || 'One line per row'}
                        size='small'
                        fullWidth
                        required
                        multiline
                        minRows={3}
                        maxRows={6}
                    />
                    <TextField
                        label='Post Code'
                        value={formValues.postCode}
                        onChange={handleFormChange('postCode')}
                        onBlur={handleBlur('postCode')}
                        error={!!getFieldError('postCode')}
                        helperText={getFieldError('postCode') || 'e.g. SW1A 1AA'}
                        size='small'
                        fullWidth
                        required
                    />
                </Box>
            </AvlModal>

            <ConfirmationModal
                open={isDeleteModalOpen}
                handleClose={handleCloseDeleteModal}
                handleConfirm={handleConfirmDelete}
                title='Delete Address'
                content={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
            />
        </>
    );
};
